import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getUsers, createUser, updateUser, deleteUser, assignBrands } from '../../services/userService';
import { getBrands } from '../../services/brandService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'delete', 'brands'
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    password: '',
    role: 'user',
    brands: []
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersData, brandsData] = await Promise.all([
          getUsers(),
          getBrands()
        ]);
        setUsers(usersData);
        setBrands(brandsData);
      } catch (err) {
        setError('Veriler yüklenirken bir hata oluştu.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = (type, user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        id: user.id || user._id,
        username: user.username,
        password: '',
        role: user.role,
        brands: user.brands ? user.brands.map(brand => brand._id || brand.id) : []
      });
    } else {
      setFormData({
        id: '',
        username: '',
        password: '',
        role: 'user',
        brands: []
      });
    }
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandsChange = (event) => {
    setFormData(prev => ({ ...prev, brands: event.target.value }));
  };

  const handleSubmit = async () => {
    try {
      if (dialogType === 'create') {
        await createUser({
          username: formData.username,
          password: formData.password,
          role: formData.role
        });
        setSnackbar({
          open: true,
          message: 'Kullanıcı başarıyla oluşturuldu.',
          severity: 'success'
        });
      } else if (dialogType === 'edit') {
        const updateData = {
          username: formData.username,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateUser(formData.id, updateData);
        setSnackbar({
          open: true,
          message: 'Kullanıcı başarıyla güncellendi.',
          severity: 'success'
        });
      } else if (dialogType === 'delete') {
        await deleteUser(formData.id);
        setSnackbar({
          open: true,
          message: 'Kullanıcı başarıyla silindi.',
          severity: 'success'
        });
      } else if (dialogType === 'brands') {
        if (!formData.id || formData.id === 'undefined' || formData.id === 'null') {
          setSnackbar({
            open: true,
            message: 'Geçerli bir kullanıcı seçilmedi. Lütfen sayfayı yenileyip tekrar deneyin.',
            severity: 'error'
          });
          return;
        }
        
        await assignBrands(formData.id, { brands: formData.brands });
        setSnackbar({
          open: true,
          message: 'Markalar başarıyla atandı.',
          severity: 'success'
        });
      }

      const updatedUsers = await getUsers();
      setUsers(updatedUsers);
      handleCloseDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Bir hata oluştu: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
      console.error(err);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h1">Kullanıcı Yönetimi</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          Yeni Kullanıcı
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Kullanıcı Adı</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Markalar</TableCell>
              <TableCell>Oluşturulma</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      user.role === 'admin'
                        ? 'Admin'
                        : user.role === 'editor'
                          ? 'Editör'
                          : user.role === 'stakeholder'
                            ? 'Stakeholder'
                            : 'Kullanıcı'
                    }
                    color={
                      user.role === 'admin'
                        ? 'primary'
                        : user.role === 'editor'
                          ? 'secondary'
                          : user.role === 'stakeholder'
                            ? 'info'
                            : 'default'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.brands && user.brands.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {user.brands.map(brand => (
                        <Chip 
                          key={brand._id || brand.id || `brand-${brand.name}`} 
                          label={brand.name} 
                          size="small" 
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Marka atanmamış
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                </TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpenDialog('brands', user)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="info" onClick={() => handleOpenDialog('edit', user)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleOpenDialog('delete', user)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog && (dialogType === 'create' || dialogType === 'edit')} onClose={handleCloseDialog}>
        <DialogTitle>{dialogType === 'create' ? 'Yeni Kullanıcı Ekle' : 'Kullanıcıyı Düzenle'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="username"
            label="Kullanıcı Adı"
            type="text"
            fullWidth
            value={formData.username}
            onChange={handleChange}
            required
          />
          <TextField
            margin="dense"
            name="password"
            label={dialogType === 'create' ? 'Şifre' : 'Şifre (değiştirmek için doldurun)'}
            type="password"
            fullWidth
            value={formData.password}
            onChange={handleChange}
            required={dialogType === 'create'}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Rol</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="editor">Editör</MenuItem>
              <MenuItem value="user">Kullanıcı</MenuItem>
              <MenuItem value="stakeholder">Stakeholder (salt okunur)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} color="primary">
            {dialogType === 'create' ? 'Ekle' : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDialog && dialogType === 'delete'} onClose={handleCloseDialog}>
        <DialogTitle>Kullanıcıyı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>{selectedUser?.username}</b> kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Brands Dialog */}
      <Dialog open={openDialog && dialogType === 'brands'} onClose={handleCloseDialog}>
        <DialogTitle>Marka Ata</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>{selectedUser?.username}</b> kullanıcısına atamak istediğiniz markaları seçin:
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel>Markalar</InputLabel>
            <Select
              multiple
              value={formData.brands}
              onChange={handleBrandsChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const brand = brands.find(b => b._id === value);
                    return <Chip key={value} label={brand ? brand.name : value} size="small" />;
                  })}
                </Box>
              )}
            >
              {brands.map((brand) => (
                <MenuItem key={brand._id} value={brand._id}>
                  {brand.name} {brand.prefix && `(${brand.prefix})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement; 